import { AppState } from '@/context/appContext';
import { canvasCoordinatesToPdfCoordinates } from '@/helpers';
import { ImageItem, PageImageItems, PageItems } from '@/types';
import { generateId } from '@/helpers/ids';

export enum ImageItemsReducerActionType {
  ADD = 'ADD_IMAGE',
  DUPLICATE = 'DUPLICATE_IMAGE',
  UPDATE = 'UPDATE_IMAGE',
  DELETE = 'DELETE_IMAGE',
  ROTATE = 'ROTATE_IMAGE',
  UPDATE_IMAGE_PAGES = 'UPDATE_IMAGE_PAGES',
}

export type ImageItemsReducerAction =
  | {
      type: ImageItemsReducerActionType.ADD;
      payload: {
        id: string;
        pageNumber: number;
        position: { x: number; y: number };
        url: string;
        imageId: string;
        pageItems: PageItems;
        scale: number;
        imageMeta: { height: number; width: number };
        /**
         * Which toolbar the asset came from. Images and signatures are inserted through
         * the same flow and are indistinguishable once placed; this is carried purely so
         * action tracking can tell ADD_IMAGE from ADD_SIGNATURE.
         */
        kind?: 'image' | 'signature';
      };
    }
  | {
      type: ImageItemsReducerActionType.DELETE;
      payload: { id: string };
    }
  | {
      type: ImageItemsReducerActionType.UPDATE;
      payload: {
        updatedImageItem: {
          id: string;
        } & Partial<ImageItem>;
      };
    }
  | {
      type: ImageItemsReducerActionType.UPDATE_IMAGE_PAGES;
      payload: { images: PageImageItems };
    }
  | {
      type: ImageItemsReducerActionType.DUPLICATE;
      payload: {
        pageItems: PageItems;
        imageItem: ImageItem;
        pageNumber: number;
        scale: number;
      };
    };

const pageImagesReducer = (state: AppState, action: ImageItemsReducerAction): AppState => {
  const { images: pageImageItems } = state;

  switch (action.type) {
    case ImageItemsReducerActionType.ADD: {
      const {
        id,
        url,
        imageId,
        pageItems,
        scale,
        imageMeta: { height, width },
        pageNumber,
        position: { x, y },
      } = action.payload;

      const newImageItem: ImageItem = {
        id,
        fileId: imageId,
        url,
        currentCanvasCoordinates: { x, y, width, height },
        currentPdfCoordinates: canvasCoordinatesToPdfCoordinates(
          { x, y: y + height, width, height },
          pageItems[pageNumber].page,
          scale,
        ),
      };

      const newItems = { ...pageImageItems };
      if (newItems[pageNumber]) {
        newItems[pageNumber] = [...newItems[pageNumber], newImageItem];
      } else {
        newItems[pageNumber] = [newImageItem];
      }

      return { ...state, images: newItems };
    }
    case ImageItemsReducerActionType.UPDATE: {
      const { updatedImageItem } = action.payload;
      const newItems: PageImageItems = { ...pageImageItems };

      // TODO: add page number in payload
      // TODO optimize this to quit after finding the element
      Object.keys(newItems).forEach((pageNumber) => {
        newItems[pageNumber] = newItems[pageNumber].map((imageItem) =>
          imageItem.id === updatedImageItem.id ? { ...imageItem, ...updatedImageItem } : imageItem,
        );
      });

      return { ...state, images: newItems };
    }
    case ImageItemsReducerActionType.DELETE: {
      const newItems: PageImageItems = {};

      // TODO: add page number in payload
      Object.keys(pageImageItems).forEach((pageNumber) => {
        newItems[pageNumber] = pageImageItems[pageNumber].filter((imageItem) => imageItem.id !== action.payload.id);
      });

      return { ...state, images: newItems };
    }
    case ImageItemsReducerActionType.DUPLICATE: {
      const { imageItem, pageItems, pageNumber, scale } = action.payload;
      const newItems: PageImageItems = { ...pageImageItems };
      const canvasCoordinates = {
        ...imageItem.currentCanvasCoordinates,
        x: imageItem.currentCanvasCoordinates.x + 10,
        y: imageItem.currentCanvasCoordinates.y + 10,
      };

      const newImageItem: ImageItem = {
        ...imageItem,
        id: generateId(),
        currentCanvasCoordinates: canvasCoordinates,
        currentPdfCoordinates: canvasCoordinatesToPdfCoordinates(canvasCoordinates, pageItems[pageNumber].page, scale),
      };

      if (newItems[pageNumber]) {
        newItems[pageNumber] = [...newItems[pageNumber], newImageItem];
      } else {
        newItems[pageNumber] = [newImageItem];
      }

      return { ...state, images: newItems };
    }
    case ImageItemsReducerActionType.UPDATE_IMAGE_PAGES: {
      return { ...state, images: { ...action.payload.images } };
    }
    default:
      return state;
  }
};

export default pageImagesReducer;
